import { ItemFromIntention } from '@interfaces/analysis/orders-intentions-for-approval/item-from-intention';
import { ItemFromOrderAnalytical } from '@interfaces/analysis/orders-intentions-for-approval/item-from-order-analytical';
import { ItemFromOrderSynthetic } from '@interfaces/analysis/orders-intentions-for-approval/item-from-order-synthetic';
import { DadosPii } from '@interfaces/analysis/campaign-pii-data';
import { SelectOptionInterface } from '../../../@types/entity/interface/select-option-interface';
import React, { createContext, useContext, useState } from 'react';

interface OrderIntentionsListsContextType {
  approveAnalitycal: (items: ItemFromOrderAnalytical[], obs: string) => void;
  approveSynthetic: (items: ItemFromOrderSynthetic[], obs: string) => void;
  approveIntention: (items: ItemFromIntention[], obs: string, dadosPii?: DadosPii) => void;
  rejectAnalitycal: (items: ItemFromOrderAnalytical[], obs: string) => void;
  rejectSynthetic: (items: ItemFromOrderSynthetic[], obs: string) => void;
  rejectIntention: (idIntencao: number, obs: string) => void;
  setItems: React.Dispatch<React.SetStateAction<(ItemFromOrderAnalytical & ItemFromOrderSynthetic & ItemFromIntention)[]>>;
  items: (ItemFromOrderAnalytical & ItemFromOrderSynthetic & ItemFromIntention)[];
  dataInicio: string;
  setDataInicio: React.Dispatch<React.SetStateAction<string>>;
  dataFim: string;
  setDataFim: React.Dispatch<React.SetStateAction<string>>;
  observacao: string;
  setObservacao: React.Dispatch<React.SetStateAction<string>>;
  isGestorOrAdmin: boolean;
  setIsGestorOrAdmin: React.Dispatch<React.SetStateAction<boolean>>;
  meliOptions: SelectOptionInterface[] | undefined;
}

export const OrderIntentionsListsContext = createContext<OrderIntentionsListsContextType | undefined>(undefined);

interface ProviderProps {
  children: React.ReactNode;
  approveOrderAnalitycal: (items: ItemFromOrderAnalytical[], obs: string) => void;
  approveOrderSynthetic: (items: ItemFromOrderSynthetic[], obs: string) => void;
  approveIntentionCampaign: (items: ItemFromIntention[], obs: string, dadosPii?: DadosPii) => void;
  rejectOrderAnalitycal: (items: ItemFromOrderAnalytical[], obs: string) => void;
  rejectOrderSynthetic: (items: ItemFromOrderSynthetic[], obs: string) => void;
  rejectIntentionCampaign: (idIntencao: number, obs: string) => void;
  setItems: React.Dispatch<React.SetStateAction<(ItemFromOrderAnalytical & ItemFromOrderSynthetic & ItemFromIntention)[]>>;
  items: (ItemFromOrderAnalytical & ItemFromOrderSynthetic & ItemFromIntention)[];
  dataInicio: string;
  setDataInicio: React.Dispatch<React.SetStateAction<string>>;
  dataFim: string;
  setDataFim: React.Dispatch<React.SetStateAction<string>>;
  isGestorOrAdmin: boolean;
  setIsGestorOrAdmin: React.Dispatch<React.SetStateAction<boolean>>;
  meliOptions: SelectOptionInterface[] | undefined;
}

export const OrderIntentionsListsProvider: React.FC<ProviderProps> = ({
  children,
  items,
  setItems,
  approveOrderAnalitycal,
  approveOrderSynthetic,
  approveIntentionCampaign,
  rejectOrderAnalitycal,
  rejectOrderSynthetic,
  rejectIntentionCampaign,
  dataInicio,
  setDataInicio,
  dataFim,
  setDataFim,
  isGestorOrAdmin,
  setIsGestorOrAdmin,
  meliOptions
}) => {
  const [observacao, setObservacao] = useState('');

  const approveAnalitycal = async (items: ItemFromOrderAnalytical[], obs: string) => {
    await approveOrderAnalitycal(items, obs);
  };

  const approveSynthetic = async (items: ItemFromOrderSynthetic[], obs: string) => {
    await approveOrderSynthetic(items, obs);
  };

  const approveIntention = async (items: ItemFromIntention[], obs: string, dadosPii?: DadosPii) => {
    await approveIntentionCampaign(items, obs, dadosPii);
  };

  const rejectAnalitycal = async (items: ItemFromOrderAnalytical[], obs: string) => {
    await rejectOrderAnalitycal(items, obs);
  };

  const rejectSynthetic = async (items: ItemFromOrderSynthetic[], obs: string) => {
    await rejectOrderSynthetic(items, obs);
  };

  const rejectIntention = async (idIntencao: number, obs: string) => {
    await rejectIntentionCampaign(idIntencao, obs);
  };

  return (
    <OrderIntentionsListsContext.Provider
      value={{
        items,
        setItems,
        dataInicio,
        setDataInicio,
        dataFim,
        setDataFim,
        approveAnalitycal,
        approveSynthetic,
        approveIntention,
        rejectAnalitycal,
        rejectSynthetic,
        rejectIntention,
        observacao,
        setObservacao,
        isGestorOrAdmin,
        setIsGestorOrAdmin,
        meliOptions
      }}
    >
      {children}
    </OrderIntentionsListsContext.Provider>
  );
};

export const useOrderIntentionsLists = () => {
  const context = useContext(OrderIntentionsListsContext);
  if (!context) {
    throw new Error('useOrderIntentionsLists must be used within a OrderIntentionsListsContext');
  }
  return context;
};
